import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  STARTER_TEMPLATES,
  StarterTemplate,
  getStarterCategoryLabel,
} from '../grapes-email-editor/starter-templates';

/**
 * Modal de selección de plantilla inicial para arrancar un email.
 *
 * Renderiza cada starter como una card con preview HTML real escalado dentro
 * de un iframe (no thumbnail estático — el preview siempre está sincronizado
 * con la definición real).
 *
 * Emite el HTML elegido para que el padre lo cargue en el editor con
 * `editor.setComponents(html)`.
 */
@Component({
  selector: 'app-starter-template-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './starter-template-modal.component.html',
})
export class StarterTemplateModalComponent implements OnChanges, AfterViewInit {
  @Input() isOpen = false;
  @Output() selected = new EventEmitter<StarterTemplate>();
  @Output() cancelled = new EventEmitter<void>();

  @ViewChildren('previewFrame') previewFrames!: QueryList<ElementRef<HTMLIFrameElement>>;

  templates = STARTER_TEMPLATES;
  hoveredId: string | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']?.currentValue) {
      // Re-renderizar los previews cuando el modal se abre.
      setTimeout(() => this.renderPreviews(), 0);
    }
  }

  ngAfterViewInit(): void {
    if (this.isOpen) this.renderPreviews();
  }

  private renderPreviews(): void {
    if (!this.previewFrames) return;
    this.previewFrames.forEach((frame, i) => {
      const tpl = this.templates[i];
      if (!tpl || !frame.nativeElement.contentDocument) return;
      const doc = frame.nativeElement.contentDocument;
      doc.open();
      doc.write(tpl.html || '<div style="padding:40px;text-align:center;color:#9ca3af;font-family:sans-serif;">En blanco</div>');
      doc.close();
    });
  }

  categoryLabel(c: StarterTemplate['category']): string {
    return getStarterCategoryLabel(c);
  }

  categoryBadgeClass(c: StarterTemplate['category']): string {
    switch (c) {
      case 'BLANK': return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300';
      case 'TRANSACTIONAL': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
      case 'MARKETING': return 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300';
      case 'OUTREACH': return 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300';
    }
  }

  pick(tpl: StarterTemplate): void {
    this.selected.emit(tpl);
  }

  cancel(): void {
    this.cancelled.emit();
  }

  trackById = (_: number, t: StarterTemplate) => t.id;
}
